/**
 * @file request.ts
 *
 * Thin request helpers for the shop admin API, layered on the shared
 * {@link apiFetch} wrapper (auth headers, credentials, 401 handling). `shopGet`
 * returns the paginated {@link WcResult} envelope; `shopMutate` returns the
 * decoded body. Both throw {@link WcApiError} on a non-OK status. String `name`
 * fields are HTML-entity decoded via {@link normalizeShopPayload}.
 */

import { apiFetch } from '../../api/client';
import { normalizeShopPayload } from './normalize';
import { WcApiError, type WcResult } from './types';

/** Base path for the shop admin endpoints under the commerce-engine API. */
export const SHOP_API_BASE = '/shop';

async function toError(response: Response): Promise<WcApiError> {
  let message = `Shop API error: ${response.status}`;
  let code = '';
  try {
    const body = (await response.json()) as { message?: string; code?: string };
    message = body?.message || message;
    code = body?.code ?? '';
  } catch {
    // Non-JSON error body; keep the status-derived message.
  }
  return new WcApiError(message, response.status, code);
}

/**
 * Performs a GET and returns the data with pagination totals.
 *
 * @param path - Path under {@link SHOP_API_BASE}, e.g. `"/products?page=1"`.
 *
 * @returns The decoded payload wrapped in a {@link WcResult}.
 *
 * @throws {WcApiError} When the response status is not OK.
 */
export async function shopGet<T>(path: string): Promise<WcResult<T>> {
  const response = await apiFetch(`${SHOP_API_BASE}${path}`);
  if (!response.ok) {
    throw await toError(response);
  }
  const total = Number(response.headers.get('X-Total-Count') ?? '0');
  const totalPages = Number(response.headers.get('X-Total-Pages') ?? '0');
  const data = normalizeShopPayload((await response.json()) as T);
  return { data, total, totalPages };
}

/**
 * Performs a mutating request (POST/PUT/DELETE) and returns the decoded body.
 *
 * @param path - Path under {@link SHOP_API_BASE}.
 * @param method - The HTTP method.
 * @param body - Optional JSON request body.
 *
 * @returns The decoded response body.
 *
 * @throws {WcApiError} When the response status is not OK.
 */
export async function shopMutate<T>(
  path: string,
  method: 'POST' | 'PUT' | 'DELETE',
  body?: unknown,
): Promise<T> {
  const response = await apiFetch(`${SHOP_API_BASE}${path}`, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    throw await toError(response);
  }
  return normalizeShopPayload((await response.json()) as T);
}
